<template>
  <div class="glass-card p-6 rounded-xl border border-slate-700">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-2xl font-bold text-white mb-1 flex items-center gap-2">
          <i class="ph-bold ph-chat-circle-dots text-blue-400"></i>
          问题反馈管理
        </h2>
        <p class="text-slate-400 text-sm">查看并处理用户提交的系统反馈与故障报告</p>
      </div>
    </div>

    <!-- Toolbar -->
    <div class="flex items-center justify-between gap-4 mb-4">
      <div class="flex items-center gap-3 flex-1">
        <!-- Search -->
        <div class="relative">
          <i class="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-base"></i>
          <input
            v-model="searchTerm"
            type="text"
            placeholder="搜索反馈内容、用户或ID..."
            class="bg-slate-900/90 border border-slate-700 text-slate-200 text-sm rounded-lg pl-10 pr-4 py-2 w-80 focus:outline-none focus:border-blue-500 transition-colors"
            @input="handleSearch"
          />
        </div>

        <!-- Filter Status -->
        <div class="relative filter-status-dropdown">
          <button
            type="button"
            @click.stop="filterDropdownOpen = !filterDropdownOpen"
            class="min-w-[140px] pl-3 pr-8 py-2 bg-slate-900/90 border border-slate-700 rounded-lg text-sm text-white text-left focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 transition-all shadow-[0_0_0_1px_rgba(59,130,246,0.25)] flex items-center justify-between"
          >
            <span class="truncate">{{ filterStatusLabel }}</span>
            <i class="ph-bold ph-caret-down text-slate-400 text-xs"></i>
          </button>
          <div
            v-if="filterDropdownOpen"
            class="absolute right-0 mt-2 w-40 bg-slate-900/95 border border-slate-700 rounded-xl shadow-2xl backdrop-blur p-1 z-20"
            @click.stop
          >
            <button
              v-for="opt in filterStatusOptions"
              :key="opt.value"
              type="button"
              @click="selectFilterStatus(opt.value)"
              class="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-200 hover:bg-blue-500/15 hover:text-white transition flex items-center justify-between"
              :class="filterStatus === opt.value ? 'bg-blue-500/10 border border-blue-500/40' : 'border border-transparent'"
            >
              <span>{{ opt.label }}</span>
              <i v-if="filterStatus === opt.value" class="ph-bold ph-check text-blue-400 text-xs"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="text-slate-400 text-sm">
        共 <span class="text-white font-semibold">{{ filteredData.length }}</span> 条记录
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-12 text-slate-400">
      <i class="ph-bold ph-spinner text-2xl animate-spin mb-2"></i>
      <p>加载中...</p>
    </div>

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <div class="border border-slate-700 rounded-xl overflow-hidden bg-slate-900/50">
        <table class="w-full text-left border-collapse">
          <thead class="bg-slate-800/80 sticky top-0 z-10">
            <tr>
              <th class="p-4 text-xs font-medium text-slate-400 uppercase tracking-wider w-16 text-center">ID</th>
              <th class="p-4 text-xs font-medium text-slate-400 uppercase tracking-wider w-32">反馈类型</th>
              <th class="p-4 text-xs font-medium text-slate-400 uppercase tracking-wider w-32">提交用户</th>
              <th class="p-4 text-xs font-medium text-slate-400 uppercase tracking-wider">反馈内容详情</th>
              <th class="p-4 text-xs font-medium text-slate-400 uppercase tracking-wider w-36">联系方式</th>
              <th class="p-4 text-xs font-medium text-slate-400 uppercase tracking-wider w-32">提交时间</th>
              <th class="p-4 text-xs font-medium text-slate-400 uppercase tracking-wider w-28">状态</th>
              <th class="p-4 text-xs font-medium text-slate-400 uppercase tracking-wider w-40 text-right">操作</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-800">
            <tr
              v-for="item in paginatedData"
              :key="item.id"
              class="hover:bg-slate-800/50 transition-colors group"
            >
              <td class="p-4 text-sm text-slate-400 font-mono text-center">#{{ item.id }}</td>
              <td class="p-4">
                <span
                  :class="[
                    'inline-flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-medium',
                    getTypeInfo(item.type).color
                  ]"
                >
                  <i :class="[getTypeInfo(item.type).icon, 'text-sm']"></i>
                  {{ getTypeInfo(item.type).label }}
                </span>
              </td>
              <td class="p-4 text-sm text-slate-300 font-medium">
                <div class="flex items-center gap-2">
                  <div
                    class="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-xs text-slate-300"
                  >
                    {{ item.userName ? item.userName.charAt(0).toUpperCase() : '?' }}
                  </div>
                  {{ item.userName || '匿名用户' }}
                </div>
              </td>
              <td class="p-4">
                <div
                  class="text-sm text-slate-200 max-w-md truncate"
                  :title="item.description"
                >
                  {{ item.description }}
                </div>
              </td>
              <td class="p-4 text-sm text-slate-400 font-mono">
                {{ item.contact || '-' }}
              </td>
              <td class="p-4 text-sm text-slate-400">
                {{ formatDate(item.createdAt) }}
              </td>
              <td class="p-4">
                <span :class="getStatusBadge(item.status).class">
                  <i :class="[getStatusBadge(item.status).icon, 'text-xs']"></i>
                  {{ getStatusBadge(item.status).label }}
                </span>
              </td>
              <td class="p-4 text-right">
                <div
                  class="flex items-center justify-end gap-2 opacity-80 group-hover:opacity-100 transition-opacity"
                >
                  <button
                    v-if="item.status !== 2 && item.status !== 3"
                    @click="handleResolveClick(item.id)"
                    class="p-1.5 text-slate-400 hover:text-green-400 hover:bg-green-400/10 rounded transition-colors"
                    title="标记为已解决"
                  >
                    <i class="ph-bold ph-check-circle text-base"></i>
                  </button>
                  <button
                    v-if="item.status === 0"
                    @click="handleStatusChange(item.id, 1)"
                    class="p-1.5 text-slate-400 hover:text-blue-400 hover:bg-blue-400/10 rounded transition-colors"
                    title="标记为处理中"
                  >
                    <i class="ph-bold ph-clock text-base"></i>
                  </button>
                  <button
                    v-if="item.status !== 0"
                    @click="handleStatusChange(item.id, 0)"
                    class="p-1.5 text-slate-400 hover:text-orange-400 hover:bg-orange-400/10 rounded transition-colors"
                    title="撤销，恢复为待处理"
                  >
                    <i class="ph-bold ph-arrow-counter-clockwise text-base"></i>
                  </button>
                  <button
                    @click="handleViewDetail(item)"
                    class="p-1.5 text-slate-400 hover:text-blue-400 hover:bg-blue-400/10 rounded transition-colors"
                    title="查看详情"
                  >
                    <i class="ph-bold ph-eye text-base"></i>
                  </button>
                  <button
                    @click="handleDelete(item.id)"
                    class="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                    title="删除"
                  >
                    <i class="ph-bold ph-trash text-base"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="paginatedData.length === 0">
              <td colspan="8" class="p-12 text-center text-slate-500">
                <div class="flex flex-col items-center gap-3">
                  <i class="ph-bold ph-magnifying-glass text-4xl text-slate-700"></i>
                  <p>未找到匹配的反馈记录</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination Footer -->
        <div
          v-if="filteredData.length > 0"
          class="px-4 py-3 border-t border-slate-800 bg-slate-800/30 flex items-center justify-between"
        >
          <div class="text-xs text-slate-500">
            显示 {{ (page - 1) * pageSize + 1 }} 到 {{ Math.min(page * pageSize, filteredData.length) }} 条，共
            {{ filteredData.length }} 条记录
          </div>
          <div class="flex gap-2">
            <button
              @click="handlePageChange(page - 1)"
              :disabled="page <= 1"
              class="px-3 py-1 text-xs rounded border border-slate-700 text-slate-400 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              上一页
            </button>
            <button
              v-for="p in visiblePages"
              :key="p"
              @click="handlePageChange(p)"
              :class="[
                'px-3 py-1 text-xs rounded border transition-colors',
                p === page
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'border-slate-700 text-slate-400 hover:bg-slate-700'
              ]"
            >
              {{ p }}
            </button>
            <button
              @click="handlePageChange(page + 1)"
              :disabled="page >= totalPages"
              class="px-3 py-1 text-xs rounded border border-slate-700 text-slate-400 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Resolve Modal (标记为已解决) -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="showResolveModal"
          class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          @click.self="cancelResolve"
        >
          <div class="bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 p-6 max-w-md w-full mx-4">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <i class="ph-bold ph-check-circle text-2xl text-green-400"></i>
              </div>
              <div>
                <h3 class="text-lg font-bold text-slate-100">标记为已解决</h3>
                <p class="text-sm text-slate-400">请填写处理意见（选填）</p>
              </div>
            </div>
            <div class="mb-6">
              <label class="block text-sm font-semibold text-slate-300 mb-2">
                管理员备注（处理意见）
              </label>
              <textarea
                v-model="resolveRemark"
                placeholder="请输入处理意见或备注信息..."
                class="w-full h-32 bg-slate-900/50 border border-slate-700 rounded-lg p-3 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500/50 transition-all resize-none text-sm leading-relaxed"
                maxlength="500"
              ></textarea>
              <div class="text-xs text-slate-500 mt-1 text-right">
                {{ resolveRemark.length }}/500
              </div>
            </div>
            <div class="flex gap-3 justify-end">
              <button
                @click="cancelResolve"
                class="px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition-colors font-medium"
              >
                取消
              </button>
              <button
                @click="confirmResolve"
                class="px-4 py-2 text-sm bg-green-600 hover:bg-green-500 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
              >
                <i class="ph-bold ph-check-circle"></i>
                <span>确认标记</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Delete Confirm Modal -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="showDeleteModal"
          class="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
          @click.self="cancelDelete"
        >
          <div class="bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 p-6 max-w-md w-full mx-4">
            <div class="flex items-center gap-4 mb-4">
              <div class="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <i class="ph-bold ph-warning text-2xl text-red-400"></i>
              </div>
              <div>
                <h3 class="text-lg font-bold text-slate-100">确认删除</h3>
                <p class="text-sm text-slate-400">此操作不可恢复</p>
              </div>
            </div>
            <p class="text-slate-300 mb-6">
              确定要删除这条反馈记录吗？删除后将无法恢复。
            </p>
            <div class="flex gap-3 justify-end">
              <button
                @click="cancelDelete"
                class="px-4 py-2 text-sm text-slate-300 hover:text-white hover:bg-slate-700 rounded-lg transition-colors font-medium"
              >
                取消
              </button>
              <button
                @click="confirmDelete"
                class="px-4 py-2 text-sm bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors flex items-center gap-2 font-medium"
              >
                <i class="ph-bold ph-trash"></i>
                <span>确认删除</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Detail Modal -->
    <Teleport to="body">
      <Transition name="fade">
        <div
          v-if="showDetailModal"
          class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          @click.self="showDetailModal = false"
        >
          <div
            class="relative w-full max-w-2xl bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 flex flex-col max-h-[90vh] overflow-hidden"
          >
            <div class="flex items-center justify-between px-6 py-5 border-b border-slate-700">
              <h3 class="text-xl font-bold text-white">反馈详情</h3>
              <button
                @click="showDetailModal = false"
                class="text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-full"
              >
                <i class="ph-bold ph-x text-xl"></i>
              </button>
            </div>
            <div v-if="selectedItem" class="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label class="text-sm font-semibold text-slate-400 mb-1 block">反馈类型</label>
                <span
                  :class="[
                    'inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm font-medium',
                    getTypeInfo(selectedItem.type).color
                  ]"
                >
                  <i :class="[getTypeInfo(selectedItem.type).icon, 'text-base']"></i>
                  {{ getTypeInfo(selectedItem.type).label }}
                </span>
              </div>
              <div>
                <label class="text-sm font-semibold text-slate-400 mb-1 block">提交用户</label>
                <p class="text-slate-200">{{ selectedItem.userName || '匿名用户' }}</p>
              </div>
              <div>
                <label class="text-sm font-semibold text-slate-400 mb-1 block">反馈内容</label>
                <p class="text-slate-200 whitespace-pre-wrap">{{ selectedItem.description }}</p>
              </div>
              <div>
                <label class="text-sm font-semibold text-slate-400 mb-1 block">联系方式</label>
                <p class="text-slate-200">{{ selectedItem.contact || '未提供' }}</p>
              </div>
              <div>
                <label class="text-sm font-semibold text-slate-400 mb-1 block">提交时间</label>
                <p class="text-slate-200">{{ formatDateTime(selectedItem.createdAt) }}</p>
              </div>
              <div>
                <label class="text-sm font-semibold text-slate-400 mb-1 block">当前状态</label>
                <span :class="getStatusBadge(selectedItem.status).class">
                  <i :class="[getStatusBadge(selectedItem.status).icon, 'text-sm']"></i>
                  {{ getStatusBadge(selectedItem.status).label }}
                </span>
              </div>
              <div>
                <label class="text-sm font-semibold text-slate-400 mb-1 block">管理员备注</label>
                <p class="text-slate-200 whitespace-pre-wrap">{{ selectedItem.adminRemark || '暂无备注' }}</p>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';

const props = defineProps({
  feedbacks: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  page: {
    type: Number,
    default: 1
  },
  pageSize: {
    type: Number,
    default: 10
  },
  total: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['load', 'page-change', 'status-change', 'delete']);

const searchTerm = ref('');
const filterStatus = ref('all');
const filterDropdownOpen = ref(false);
const showDetailModal = ref(false);
const selectedItem = ref(null);

// 筛选状态选项
const filterStatusOptions = [
  { value: 'all', label: '全部状态' },
  { value: '0', label: '待处理' },
  { value: '1', label: '处理中' },
  { value: '2', label: '已解决' },
  { value: '3', label: '已关闭' }
];

// 获取当前筛选状态标签
const filterStatusLabel = computed(() => {
  const option = filterStatusOptions.find(opt => opt.value === filterStatus.value);
  return option ? option.label : '全部状态';
});

// 选择筛选状态
const selectFilterStatus = (value) => {
  filterStatus.value = value;
  filterDropdownOpen.value = false;
  handleFilterChange();
};

// 获取反馈类型信息
const getTypeInfo = (type) => {
  const typeMap = {
    bug: { icon: 'ph-bold ph-bug', label: '功能异常', color: 'text-red-400 bg-red-400/10 border-red-400/20' },
    ai_error: { icon: 'ph-bold ph-robot', label: 'AI故障', color: 'text-purple-400 bg-purple-400/10 border-purple-400/20' },
    lag: { icon: 'ph-bold ph-hourglass', label: '性能卡顿', color: 'text-orange-400 bg-orange-400/10 border-orange-400/20' },
    subaccount: { icon: 'ph-bold ph-user-plus', label: '子账号失败', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
    address: { icon: 'ph-bold ph-map-pin', label: '地址问题', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
    captcha: { icon: 'ph-bold ph-envelope', label: '验证码问题', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' },
    upload: { icon: 'ph-bold ph-cloud-arrow-up', label: '文件上传', color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20' },
    processing: { icon: 'ph-bold ph-clock', label: '处理超时', color: 'text-orange-400 bg-orange-400/10 border-orange-400/20' },
    export: { icon: 'ph-bold ph-database', label: '数据导出', color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20' },
    download: { icon: 'ph-bold ph-download', label: '文件下载', color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/20' },
    design: { icon: 'ph-bold ph-monitor', label: 'UI/体验', color: 'text-indigo-400 bg-indigo-400/10 border-indigo-400/20' },
    performance: { icon: 'ph-bold ph-lightning', label: '性能优化', color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20' },
    billing: { icon: 'ph-bold ph-credit-card', label: '计费问题', color: 'text-green-400 bg-green-400/10 border-green-400/20' },
    feature: { icon: 'ph-bold ph-lightbulb', label: '功能建议', color: 'text-blue-400 bg-blue-400/10 border-blue-400/20' },
    security: { icon: 'ph-bold ph-shield', label: '安全漏洞', color: 'text-red-400 bg-red-400/10 border-red-400/20' },
    account: { icon: 'ph-bold ph-user-circle', label: '账户其他', color: 'text-slate-400 bg-slate-400/10 border-slate-400/20' },
    other: { icon: 'ph-bold ph-warning', label: '其他问题', color: 'text-slate-400 bg-slate-400/10 border-slate-400/20' }
  };
  return typeMap[type] || typeMap.other;
};

// 获取状态徽章
const getStatusBadge = (status) => {
  const statusMap = {
    0: { icon: 'ph-bold ph-clock', label: '待处理', class: 'px-2 py-1 rounded text-xs font-medium bg-slate-500/10 text-slate-400 border border-slate-500/20 flex items-center gap-1 w-fit' },
    1: { icon: 'ph-bold ph-clock-clockwise', label: '处理中', class: 'px-2 py-1 rounded text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center gap-1 w-fit' },
    2: { icon: 'ph-bold ph-check-circle', label: '已解决', class: 'px-2 py-1 rounded text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20 flex items-center gap-1 w-fit' },
    3: { icon: 'ph-bold ph-x-circle', label: '已关闭', class: 'px-2 py-1 rounded text-xs font-medium bg-slate-600/10 text-slate-500 border border-slate-600/20 flex items-center gap-1 w-fit' }
  };
  return statusMap[status] || statusMap[0];
};

// 格式化日期
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('zh-CN');
};

// 格式化日期时间
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('zh-CN');
};

// 过滤数据
const filteredData = computed(() => {
  let result = [...props.feedbacks];

  // 状态过滤
  if (filterStatus.value !== 'all') {
    result = result.filter(item => item.status === parseInt(filterStatus.value));
  }

  // 搜索过滤
  if (searchTerm.value.trim()) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(item => {
      return (
        item.description?.toLowerCase().includes(term) ||
        item.userName?.toLowerCase().includes(term) ||
        item.id?.toString().includes(term) ||
        item.contact?.toLowerCase().includes(term)
      );
    });
  }

  return result;
});

// 分页数据
const paginatedData = computed(() => {
  const start = (props.page - 1) * props.pageSize;
  const end = start + props.pageSize;
  return filteredData.value.slice(start, end);
});

// 总页数
const totalPages = computed(() => {
  return Math.ceil(filteredData.value.length / props.pageSize) || 1;
});

// 可见页码
const visiblePages = computed(() => {
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, props.page - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages.value, start + maxVisible - 1);
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

// 处理搜索
const handleSearch = () => {
  emit('load');
};

// 处理过滤
const handleFilterChange = () => {
  emit('load');
};

// 点击外部关闭下拉菜单
const handleClickOutside = (event) => {
  const dropdown = document.querySelector('.filter-status-dropdown');
  if (dropdown && !dropdown.contains(event.target)) {
    filterDropdownOpen.value = false;
  }
};

// 监听点击外部事件
watch(filterDropdownOpen, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      document.addEventListener('click', handleClickOutside);
    });
  } else {
    document.removeEventListener('click', handleClickOutside);
  }
});

// 处理状态变更
const handleStatusChange = (id, newStatus, adminRemark = null) => {
  emit('status-change', { id, status: newStatus, adminRemark });
};

// 标记为已解决相关状态
const showResolveModal = ref(false);
const resolveTargetId = ref(null);
const resolveRemark = ref('');

// 点击标记为已解决按钮
const handleResolveClick = (id) => {
  resolveTargetId.value = id;
  resolveRemark.value = '';
  showResolveModal.value = true;
};

// 确认标记为已解决
const confirmResolve = () => {
  if (resolveTargetId.value) {
    // 获取备注内容，trim 后如果为空字符串则传递空字符串，让后端决定是否更新
    const remark = resolveRemark.value.trim();
    handleStatusChange(resolveTargetId.value, 2, remark || '');
    showResolveModal.value = false;
    resolveTargetId.value = null;
    resolveRemark.value = '';
  }
};

// 取消标记为已解决
const cancelResolve = () => {
  showResolveModal.value = false;
  resolveTargetId.value = null;
  resolveRemark.value = '';
};

// 删除确认相关状态
const showDeleteModal = ref(false);
const deleteTargetId = ref(null);

// 处理删除（打开确认弹窗）
const handleDelete = (id) => {
  deleteTargetId.value = id;
  showDeleteModal.value = true;
};

// 确认删除
const confirmDelete = () => {
  if (deleteTargetId.value) {
    emit('delete', deleteTargetId.value);
    showDeleteModal.value = false;
    deleteTargetId.value = null;
  }
};

// 取消删除
const cancelDelete = () => {
  showDeleteModal.value = false;
  deleteTargetId.value = null;
};

// 查看详情
const handleViewDetail = (item) => {
  selectedItem.value = item;
  showDetailModal.value = true;
};

// 处理分页
const handlePageChange = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    emit('page-change', newPage);
  }
};

// 监听props变化，重新加载
watch(
  () => [props.feedbacks, props.page],
  () => {
    // 数据变化时，可以触发重新加载
  }
);

// 组件卸载时移除事件监听
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

